import { ScheduleType } from '../activities/activity-type.model';
import { ScheduleGroup, ScheduleGroupUpdateInput } from '../settings/me.model';

export const SCHEDULE_GROUP_ORDER: ScheduleType[] = ['PRIMARY', 'SECONDARY', 'TERTIARY', 'QUATERNARY', 'QUINARY'];

export const SCHEDULE_GROUP_DEFAULT_LABELS: Record<ScheduleType, string> = {
  PRIMARY: 'Primary',
  SECONDARY: 'Secondary',
  TERTIARY: 'Tertiary',
  QUATERNARY: 'Quaternary',
  QUINARY: 'Quinary'
};

export const SCHEDULE_GROUP_SLUGS: Record<ScheduleType, string> = {
  PRIMARY: 'cycle-1',
  SECONDARY: 'cycle-2',
  TERTIARY: 'cycle-3',
  QUATERNARY: 'cycle-4',
  QUINARY: 'cycle-5'
};

export function groupPageCommands(ordinal: ScheduleType): string[] {
  return ['/today/group', ordinal.toLowerCase()];
}

export function resolveScheduleGroupLabel(group: Pick<ScheduleGroup, 'ordinal' | 'label'>): string {
  const trimmed = group.label?.trim();
  if (trimmed) {
    return trimmed;
  }
  return SCHEDULE_GROUP_DEFAULT_LABELS[group.ordinal];
}

export function resolveScheduleTypeLabel(ordinal: ScheduleType, groups: ScheduleGroup[]): string {
  const group = groups.find((candidate) => candidate.ordinal === ordinal);
  if (!group) {
    return SCHEDULE_GROUP_DEFAULT_LABELS[ordinal];
  }
  return resolveScheduleGroupLabel(group);
}

export function getEnabledScheduleGroups(groups: ScheduleGroup[]): ScheduleGroup[] {
  const fallbackOrderByOrdinal = new Map(SCHEDULE_GROUP_ORDER.map((ordinal, index) => [ordinal, index]));
  return groups
    .filter((group) => group.enabled)
    .slice()
    .sort((left, right) => {
      const leftPosition = Number.isFinite(left.position) ? Number(left.position) : (fallbackOrderByOrdinal.get(left.ordinal) ?? 0);
      const rightPosition = Number.isFinite(right.position)
        ? Number(right.position)
        : (fallbackOrderByOrdinal.get(right.ordinal) ?? 0);
      if (leftPosition !== rightPosition) {
        return leftPosition - rightPosition;
      }
      return (fallbackOrderByOrdinal.get(left.ordinal) ?? 0) - (fallbackOrderByOrdinal.get(right.ordinal) ?? 0);
    });
}

export function scheduleTypeFromSlug(slug: string | null): ScheduleType | null {
  if (!slug) {
    return null;
  }
  const normalized = slug.trim().toLowerCase();
  return (SCHEDULE_GROUP_ORDER.find((ordinal) => SCHEDULE_GROUP_SLUGS[ordinal] === normalized) ?? null);
}

export function parseGroupOrdinal(param: string | null): ScheduleType | null {
  if (!param) {
    return null;
  }
  const normalized = param.trim().toUpperCase();
  if (SCHEDULE_GROUP_ORDER.includes(normalized as ScheduleType)) {
    return normalized as ScheduleType;
  }
  return scheduleTypeFromSlug(param);
}

export function isGroupPageUrl(url: string): boolean {
  const path = url.split('?')[0].toLowerCase();
  return path.startsWith('/today/group/');
}

export function buildScheduleGroupPositionUpdates(
  enabledOrdinals: ScheduleType[],
  currentGroups: ScheduleGroup[]
): ScheduleGroupUpdateInput[] {
  const enabledGroups = getEnabledScheduleGroups(currentGroups);
  const enabledOrdinalSet = new Set(enabledGroups.map((group) => group.ordinal));
  if (
    enabledOrdinals.length !== enabledGroups.length
    || new Set(enabledOrdinals).size !== enabledOrdinals.length
    || enabledOrdinals.some((ordinal) => !enabledOrdinalSet.has(ordinal))
  ) {
    return [];
  }

  const currentEnabledOrder = enabledGroups.map((group) => group.ordinal);
  const isUnchanged = currentEnabledOrder.every((ordinal, index) => ordinal === enabledOrdinals[index]);
  if (isUnchanged) {
    return [];
  }

  const nextPositionsByOrdinal = new Map<ScheduleType, number>();
  enabledOrdinals.forEach((ordinal, index) => nextPositionsByOrdinal.set(ordinal, index));

  const disabledOrdinals = SCHEDULE_GROUP_ORDER.filter((ordinal) => !nextPositionsByOrdinal.has(ordinal));
  disabledOrdinals.forEach((ordinal, index) => {
    nextPositionsByOrdinal.set(ordinal, enabledOrdinals.length + index);
  });

  const currentByOrdinal = new Map(currentGroups.map((group) => [group.ordinal, group]));
  return SCHEDULE_GROUP_ORDER.map((ordinal) => ({
    ordinal,
    budgetMinutes: currentByOrdinal.get(ordinal)?.budgetMinutes ?? null,
    position: nextPositionsByOrdinal.get(ordinal) ?? 0
  }));
}
